import { version } from '../../package.json';

/** What the account server needs to tell devices apart (one free trial each). */
export interface DeviceIdentity {
  platform: string;
  /** Stable id where there is no MAC address (iPhone Keychain id, LG device id). */
  id?: string;
  mac?: string | null;
  model?: string;
  os?: string;
  appVersion: string;
  /** Browsers: hash of browser and screen traits. */
  fingerprint?: string;
  /** Apple DeviceCheck token (iPhone). */
  deviceCheckToken?: string;
}

interface TvGlobals {
  webapis?: {
    network?: { getMac?: () => string };
    productinfo?: { getRealModel?: () => string; getFirmware?: () => string };
  };
  webOS?: {
    service?: {
      request: (
        uri: string,
        options: { method: string; parameters: object; onSuccess: (result: Record<string, unknown>) => void; onFailure: () => void },
      ) => void;
    };
  };
}


function lunaCall(uri: string, method: string, parameters: object): Promise<Record<string, unknown> | null> {
  const service = (globalThis as TvGlobals).webOS?.service;
  if (!service) return Promise.resolve(null);
  return new Promise((resolve) => service.request(uri, { method, parameters, onSuccess: resolve, onFailure: () => resolve(null) }));
}

/** The device's identity: MAC address on TVs and Macs, Keychain id on iPhone, stored id and fingerprint in browsers. */
export async function deviceIdentity(): Promise<DeviceIdentity | null> {
  const protocol = typeof location === 'undefined' ? '' : location.protocol;
  // Native shells: the iOS relay (ProviderRelay.swift) and the desktop main process.
  if (protocol === 'capacitor:' || protocol === 'app:') {
    try {
      const response = await fetch('viva-provider://device/');
      return response.ok ? ((await response.json()) as DeviceIdentity) : null;
    } catch {
      return null;
    }
  }
  const tv = globalThis as TvGlobals;
  if (tv.webapis?.network?.getMac) {
    try {
      return {
        platform: 'tizen',
        mac: tv.webapis.network.getMac(),
        model: tv.webapis.productinfo?.getRealModel?.() ?? '',
        os: `Tizen ${tv.webapis.productinfo?.getFirmware?.() ?? ''}`.trim(),
        appVersion: version,
      };
    } catch {
      return null;
    }
  }
  if (tv.webOS?.service) {
    const network = await lunaCall('luna://com.webos.service.connectionmanager', 'getinfo', {});
    const wired = (network?.wiredInfo as { macAddress?: string } | undefined)?.macAddress;
    const wifi = (network?.wifiInfo as { macAddress?: string } | undefined)?.macAddress;
    const ids = await lunaCall('luna://com.webos.service.sm', 'deviceid/getIDs', { idType: ['LGUDID'] });
    const udid = (ids?.idList as { idValue?: string }[] | undefined)?.[0]?.idValue;
    if (!wired && !wifi && !udid) return null;
    return { platform: 'webos', mac: wired ?? wifi ?? null, id: udid, appVersion: version };
  }
  return browserIdentity();
}

const BROWSER_ID_KEY = 'viva.deviceId';

/**
 * Browsers (phones, computers): a random id kept in local storage, plus a
 * fingerprint of browser and screen traits that stays the same when site
 * data is cleared, shown to the admin to spot repeat free trials.
 */
async function browserIdentity(): Promise<DeviceIdentity> {
  let id: string | null = null;
  try {
    id = localStorage.getItem(BROWSER_ID_KEY);
    if (!id) localStorage.setItem(BROWSER_ID_KEY, (id = randomId()));
  } catch {
    id = randomId();
  }
  return { platform: 'web', id, fingerprint: await fingerprint(), model: browserName(), os: navigator.platform, appVersion: version };
}

function randomId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function browserName(): string {
  const agent = navigator.userAgent;
  const match = /(Edg|OPR|SamsungBrowser|Firefox|CriOS|FxiOS|Chrome|Version)\/[\d.]+/.exec(agent);
  const device = /iPhone|iPad|Android|Macintosh|Windows|Linux/.exec(agent)?.[0] ?? '';
  const name = match ? match[0].replace('Version/', 'Safari/').replace('CriOS/', 'Chrome/').replace('FxiOS/', 'Firefox/') : 'Browser';
  return `${name.split('.')[0]} · ${device}`.trim();
}

async function fingerprint(): Promise<string> {
  const traits = [
    navigator.userAgent,
    navigator.language,
    (navigator.languages ?? []).join(','),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    `${screen.width}x${screen.height}x${screen.colorDepth}@${window.devicePixelRatio}`,
    navigator.hardwareConcurrency,
    (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? '',
    navigator.maxTouchPoints,
    canvasTrait(),
  ].join('|');
  try {
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(traits)));
    return Array.from(digest.slice(0, 16), (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    // No WebCrypto (plain http on a LAN address): a short non-cryptographic hash is enough for a signal.
    let hash = 0;
    for (let i = 0; i < traits.length; i++) hash = (Math.imul(31, hash) + traits.charCodeAt(i)) | 0;
    return `h${(hash >>> 0).toString(16)}`;
  }
}

/** Text drawn on a canvas renders slightly differently per device, GPU and fonts. */
function canvasTrait(): string {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 40;
    const context = canvas.getContext('2d');
    if (!context) return '';
    context.textBaseline = 'top';
    context.font = '16px Arial';
    context.fillStyle = '#e50914';
    context.fillRect(0, 0, 60, 20);
    context.fillStyle = '#0b0f1a';
    // Keep this text as it is (the old app name): changing it changes every browser's fingerprint and repeat-trial checks lose track.
    context.fillText('Viva Stream ✓ 1.0', 4, 8);
    return canvas.toDataURL().slice(-64);
  } catch {
    return '';
  }
}
